import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CircularProgress, TextField } from '@mui/material';
import { SiteProfile } from '../../types';
import { isLatitude, isLongitude, isValidCharWithoutSpace } from '../../lib/site-helper';
import { useSelector, useDispatch } from 'react-redux';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { useAuthHelper } from '../../components/AuthContextProvider';
import { Selectors } from '../../selectors';
import { Thunks } from '../../actions';
import { DECIMAL_NUMBER } from '../../constants';
import locationStyles from '../location-management/LocationStyle';
import { NavigationPromptModal } from '../../components/NavigationPromptModal/NavigationPromptModal';

import { HeaderInfo } from '../../components/HeaderInfo/HeaderInfo';

export const EditSite: React.FC<{
    siteProfileData: SiteProfile;
    updatedSiteData: Function;
    cancelAction: Function;
}> = (props) => {
    const classes = locationStyles();
    const [siteInfo, setSiteInfo] = useState<SiteProfile>(props.siteProfileData);
    const [unmodifiedLocationInfo] = useState<SiteProfile>(props.siteProfileData);
    const [isDirty, setIsDirty] = useState(false);
    const siteState = useSelector(Selectors.Site.site);
    const dispatch = useDispatch();
    const { addToast } = useToasts();
    const authHelper = useAuthHelper();

    useEffect(() => {
        const isDataModified = JSON.stringify(unmodifiedLocationInfo) !== JSON.stringify(siteInfo);
        setIsDirty(isDataModified);
    }, [unmodifiedLocationInfo, siteInfo]); //eslint-disable-line react-hooks/exhaustive-deps

    const onChangeSiteName = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedSiteName: SiteProfile
    ): void => {
        if (isValidCharWithoutSpace(e.target.value)) {
            const siteNameObj: SiteProfile = { ...modifiedSiteName };
            siteNameObj.name = e.target.value;
            setSiteInfo(siteNameObj);
        }
    };
    const onChangeNotes = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedNotes: SiteProfile
    ): void => {
        if (isValidCharWithoutSpace(e.target.value)) {
            const notesObj: SiteProfile = { ...modifiedNotes };
            notesObj.description = e.target.value;
            setSiteInfo(notesObj);
        }
    };

    const onChangeLatitude = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedLatitude: SiteProfile
    ): void => {
        const value = e.target.value;
        if (new RegExp(DECIMAL_NUMBER).test(value) || value === '') {
            const latitudeObj: SiteProfile = { ...modifiedLatitude };
            latitudeObj.latitude = value;
            setSiteInfo(latitudeObj);
        }
    };
    const onChangeLongitude = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedLongitude: SiteProfile
    ): void => {
        const value = e.target.value;
        if (new RegExp(DECIMAL_NUMBER).test(value) || value === '') {
            const longitudeObj: SiteProfile = { ...modifiedLongitude };
            longitudeObj.longitude = e.target.value;
            setSiteInfo(longitudeObj);
        }
    };

    const isDisable = (): boolean => {
        if (siteInfo)
            return (
                Boolean(siteInfo.name !== null && siteInfo.name.trimEnd() === '') ||
                isLatitude(siteInfo.latitude) ||
                isLongitude(siteInfo.longitude) ||
                !isDirty
            );
        return false;
    };

    const submit = (): void => {
        setIsDirty(false);
        dispatch(Thunks.Site.updateSiteRegistration(siteInfo.id, siteInfo, addToast, authHelper));
        props.updatedSiteData(siteInfo);
    };
    return (
        <Card style={{ padding: 24 }}>
            <HeaderInfo title="Edit Site Information" entity="when editing a Site" />
            <Box className={classes.paddingTextbox}>
                <TextField
                    inputProps={{
                        'data-testid': 'site-name',
                        maxLength: 100,
                    }}
                    fullWidth
                    required
                    variant="filled"
                    id="site"
                    label="Site Name"
                    value={siteInfo.name}
                    onChange={(e): void => onChangeSiteName(e, siteInfo)}
                    helperText={
                        <Box component="span" display="flex" justifyContent="flex-end">
                            {siteInfo.name.length}/100
                        </Box>
                    }
                />
            </Box>
            <Box className={`${classes.padding8} ${classes.paddingBottom24}`}>
                <TextField
                    inputProps={{ 'data-testid': 'latitude' }}
                    error={isLatitude(siteInfo.latitude)}
                    variant="filled"
                    helperText={isLatitude(siteInfo.latitude) ? 'Invalid Latitude' : ''}
                    id="Latitude"
                    fullWidth
                    label="GPS Latitude"
                    value={siteInfo.latitude}
                    onChange={(e): void => {
                        onChangeLatitude(e, siteInfo);
                    }}
                />
            </Box>
            <Box className={`${classes.paddingTextbox} ${classes.paddingTop16} ${classes.paddingBottom24}`}>
                <TextField
                    inputProps={{ 'data-testid': 'longitude' }}
                    fullWidth
                    error={isLongitude(siteInfo.longitude)}
                    helperText={isLongitude(siteInfo.longitude) ? 'Invalid Longitude' : ''}
                    id="longitude"
                    variant="filled"
                    label="GPS Longitude"
                    value={siteInfo.longitude}
                    onChange={(e): void => {
                        onChangeLongitude(e, siteInfo);
                    }}
                />
            </Box>
            <Box className={`${classes.paddingTextbox}`}>
                <TextField
                    inputProps={{ 'data-testid': 'notes', maxLength: 255 }}
                    id="Notes"
                    fullWidth
                    multiline
                    maxRows={Infinity}
                    label="Notes"
                    variant="filled"
                    value={siteInfo.description}
                    onChange={(e): void => {
                        onChangeNotes(e, siteInfo);
                    }}
                    helperText={
                        <Box component="span" display="flex" justifyContent="flex-end">
                            {siteInfo.description ? siteInfo.description.length : 0}/255
                        </Box>
                    }
                />
            </Box>
            <Box className={`${classes.flexContentEnd} ${classes.padding16}`}>
                <Button
                    style={{ marginRight: 16 }}
                    variant="outlined"
                    color="primary"
                    data-testid="cancel-site"
                    onClick={(): void => {
                        props.cancelAction();
                    }}
                >
                    Cancel
                </Button>
                <Button
                    startIcon={siteState.loading ? <CircularProgress size="1rem" style={{ color: 'white' }} /> : <></>}
                    disabled={isDisable()}
                    variant="contained"
                    color="primary"
                    data-testid="site-update"
                    onClick={submit}
                >
                    Submit Changes
                </Button>
            </Box>
            <NavigationPromptModal isDirty={isDirty} />
        </Card>
    );
};
